/**
 * Notification Badge Component
 *
 * Displays a badge with unread notification count.
 * Updates in real-time via WebSocket.
 */

import React, { useState, useEffect } from "react";
import {
  Badge,
  IconButton,
  Menu,
  Typography,
  Box,
  Divider,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Payment as PaymentIcon,
  School as StudentIcon,
  Receipt as InvoiceIcon,
  CheckCircle as ApprovedIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useWebSocket, WebSocketEvent } from "@/hooks/useWebSocket";
import { formatRelativeTime } from "@/utils/formatters";

interface Notification {
  id: string;
  title: string;
  body: string;
  type: string;
  action_url?: string;
  timestamp: string;
  read: boolean;
}

// Map notification types to icons
const typeIcons: Record<string, React.ReactElement> = {
  payment: <PaymentIcon color="success" />,
  invoice: <InvoiceIcon color="primary" />,
  student: <StudentIcon color="info" />,
  application: <ApprovedIcon color="success" />,
  warning: <WarningIcon color="warning" />,
  info: <InfoIcon color="action" />,
};

export const NotificationBadge: React.FC = () => {
  const navigate = useNavigate();
  const { subscribe, isConnected } = useWebSocket();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading] = useState(false);

  const open = Boolean(anchorEl);
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Subscribe to new notifications
  useEffect(() => {
    const unsubscribe = subscribe<{
      notification_id: string;
      title: string;
      body: string;
      type: string;
      action_url?: string;
    }>(WebSocketEvent.NOTIFICATION_NEW, (data, message) => {
      const newNotification: Notification = {
        id: data.notification_id,
        title: data.title,
        body: data.body,
        type: data.type,
        action_url: data.action_url,
        timestamp: message.timestamp,
        read: false,
      };

      setNotifications((prev) => [newNotification, ...prev].slice(0, 20)); // Keep last 20
    });

    return unsubscribe;
  }, [subscribe]);

  // Also subscribe to important events and convert to notifications
  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    // Payment received
    unsubscribers.push(
      subscribe<{ payment_id: string; family_name: string; amount: number }>(
        WebSocketEvent.PAYMENT_RECEIVED,
        (data, message) => {
          const notification: Notification = {
            id: `payment-${data.payment_id}`,
            title: "Payment Received",
            body: `$${Number(data.amount).toFixed(2)} from ${data.family_name}`,
            type: "payment",
            action_url: `/payments/${data.payment_id}`,
            timestamp: message.timestamp,
            read: false,
          };
          setNotifications((prev) => [notification, ...prev].slice(0, 20));
        }
      )
    );

    // Application submitted
    unsubscribers.push(
      subscribe<{ application_id: string; student_name: string }>(
        WebSocketEvent.APPLICATION_SUBMITTED,
        (data, message) => {
          const notification: Notification = {
            id: `app-${data.application_id}`,
            title: "New Application",
            body: `Application for ${data.student_name}`,
            type: "application",
            action_url: `/applications/${data.application_id}`,
            timestamp: message.timestamp,
            read: false,
          };
          setNotifications((prev) => [notification, ...prev].slice(0, 20));
        }
      )
    );

    // Enrollment created
    unsubscribers.push(
      subscribe<{ enrollment_id: string; student_name: string; class_name: string }>(
        WebSocketEvent.ENROLLMENT_CREATED,
        (data, message) => {
          const notification: Notification = {
            id: `enrollment-${data.enrollment_id}`,
            title: "New Enrollment",
            body: `${data.student_name} enrolled in ${data.class_name}`,
            type: "student",
            action_url: `/enrollments`,
            timestamp: message.timestamp,
            read: false,
          };
          setNotifications((prev) => [notification, ...prev].slice(0, 20));
        }
      )
    );

    // Discount pending approval
    unsubscribers.push(
      subscribe<{ discount_id: string; student_name: string; discount_name: string }>(
        WebSocketEvent.DISCOUNT_PENDING_APPROVAL,
        (data, message) => {
          const notification: Notification = {
            id: `discount-${data.discount_id}`,
            title: "Discount Pending Approval",
            body: `${data.discount_name} for ${data.student_name}`,
            type: "warning",
            action_url: `/discounts?tab=pending`,
            timestamp: message.timestamp,
            read: false,
          };
          setNotifications((prev) => [notification, ...prev].slice(0, 20));
        }
      )
    );

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [subscribe]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    setNotifications((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
    );

    // Navigate if action URL provided
    if (notification.action_url) {
      navigate(notification.action_url);
    }

    handleClose();
  };

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleClearAll = () => {
    setNotifications([]);
    handleClose();
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleOpen}
        aria-label={`${unreadCount} unread notifications`}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          sx={{
            "& .MuiBadge-badge": {
              animation: unreadCount > 0 ? "pulse 2s infinite" : "none",
              "@keyframes pulse": {
                "0%": { transform: "scale(1)" },
                "50%": { transform: "scale(1.1)" },
                "100%": { transform: "scale(1)" },
              },
            },
          }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: 360,
              maxHeight: 480,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" sx={{
            fontWeight: 600
          }}>
            Notifications
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Connection indicator */}
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: isConnected ? "success.main" : "error.main",
              }}
              title={isConnected ? "Connected" : "Disconnected"}
            />
            {unreadCount > 0 && (
              <Button size="small" onClick={handleMarkAllRead}>
                Mark all read
              </Button>
            )}
          </Box>
        </Box>

        <Divider />

        {/* Notifications List */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <NotificationsIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0, maxHeight: 320, overflow: "auto" }}>
            {notifications.map((notification) => (
              <ListItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  cursor: "pointer",
                  bgcolor: notification.read ? "transparent" : "action.hover",
                  "&:hover": { bgcolor: "action.selected" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {typeIcons[notification.type] || <InfoIcon color="action" />}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: notification.read ? 400 : 600
                      }}
                    >
                      {notification.title}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography variant="caption" component="span" sx={{
                        display: "block"
                      }}>
                        {notification.body}
                      </Typography>
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{
                          color: "text.disabled"
                        }}
                      >
                        {formatRelativeTime(notification.timestamp)}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}

        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1 }}>
              <Button fullWidth size="small" onClick={handleClearAll}>
                Clear all
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationBadge;
